import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { PaymentBadges } from '../components/PaymentBadges';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { 
  CheckCircle2, 
  Clock, 
  Copy, 
  QrCode,
  ArrowRight,
  Building2,
  CreditCard,
  Loader2,
  RefreshCw,
  Activity,
  Cpu
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const axios_instance = axios;
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bank logos/colors
const BANK_INFO = {
  bca: { name: 'BCA', color: '#003d79', textColor: 'white' },
  bni: { name: 'BNI', color: '#f15a22', textColor: 'white' },
  bri: { name: 'BRI', color: '#00529c', textColor: 'white' },
  mandiri: { name: 'Mandiri', color: '#003366', textColor: 'white' },
  permata: { name: 'Permata', color: '#009a44', textColor: 'white' },
  cimb: { name: 'CIMB Niaga', color: '#ec1c24', textColor: 'white' },
};

export default function Checkout() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);
  const [processing, setProcessing] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('va');
  const [selectedBank, setSelectedBank] = useState('bni');
  const [paymentChannels, setPaymentChannels] = useState(null);
  const [creatingPayment, setCreatingPayment] = useState(false);

  useEffect(() => {
    if (!order && orderId) {
      toast.error('Order tidak ditemukan');
      navigate('/');
    }
    // Fetch payment channels
    fetchPaymentChannels();
  }, [orderId, order, navigate]);

  const fetchPaymentChannels = async () => {
    try {
      const response = await axios_instance.get(`${API_URL}/api/payment/channels`);
      setPaymentChannels(response.data);
    } catch (error) {
      console.error('Failed to fetch payment channels:', error);
    }
  };

  const createPayment = async () => {
    if (!order) return;
    
    setCreatingPayment(true);
    try {
      const response = await axios_instance.post(`${API_URL}/api/payment/create`, {
        order_id: order.id,
        amount: order.price,
        customer_name: order.user_email?.split('@')[0] || 'Customer',
        customer_email: order.user_email,
        payment_method: selectedPaymentMethod,
        va_channel: selectedBank
      });
      
      if (response.data.success) {
        setPaymentData(response.data.data);
        toast.success('Payment berhasil dibuat!');
      } else {
        toast.error(response.data.message || 'Gagal membuat payment');
      }
    } catch (error) {
      console.error('Failed to create payment:', error);
      toast.error('Gagal membuat payment. Menggunakan mode simulasi.');
      // Fallback to mock mode
      setPaymentData({
        success: true,
        payment_method: selectedPaymentMethod,
        channel: selectedBank,
        va_number: '8810' + Math.random().toString().slice(2, 16),
        amount: order.price,
        is_mock: true
      });
    } finally {
      setCreatingPayment(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      // Simulate payment processing
      const response = await axios_instance.post(`${API_URL}/api/payment/process/${order.id}`);
      setOrder(response.data.order);
      toast.success('Pembayaran berhasil!');
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error('Pembayaran gagal');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Disalin ke clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const isCompleted = order.status === 'completed';

  return (
    <div className="min-h-screen pt-24 pb-12 cyber-grid">
      <div className="max-w-6xl mx-auto px-4">
        {isCompleted ? (
          /* Success State */
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 neon-green">
                <CheckCircle2 className="w-10 h-10 text-primary" />
              </div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-primary/30 bg-primary/5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                <span className="font-mono text-[10px] text-primary tracking-widest uppercase">TRANSMISSION_COMPLETE</span>
              </div>
              <h1 className="font-bold text-3xl text-white uppercase mb-2 tracking-wider">
                Transmisi Sukses!
              </h1>
              <p className="text-muted-foreground text-sm font-mono">
                Muatan aether digital telah dialirkan ke reactor target. Terima kasih Operator.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
              <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-secondary/50" />
                <h2 className="font-mono text-xs text-primary uppercase tracking-widest mb-4">▸ DETAIL MANIFEST</h2>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-border/40 pb-2">
                    <span className="font-mono text-xs text-muted-foreground uppercase">Order Number</span>
                    <span className="font-mono text-sm text-white">{order.order_number}</span>
                  </div>
                  <div className="flex justify-between border-b border-border/40 pb-2">
                    <span className="font-mono text-xs text-muted-foreground uppercase">Produk</span>
                    <span className="text-sm text-white font-medium">{order.product_name}</span>
                  </div>
                  <div className="flex justify-between border-b border-border/40 pb-2">
                    <span className="font-mono text-xs text-muted-foreground uppercase">Item</span>
                    <span className="text-sm text-white font-mono">{order.denomination_name}</span>
                  </div>
                  <div className="flex justify-between border-b border-border/40 pb-2">
                    <span className="font-mono text-xs text-muted-foreground uppercase">Target ID</span>
                    <span className="font-mono text-sm text-white">{order.game_user_id}</span>
                  </div>
                  {order.game_server_id && (
                    <div className="flex justify-between border-b border-border/40 pb-2">
                      <span className="font-mono text-xs text-muted-foreground uppercase">Server ID</span>
                      <span className="font-mono text-sm text-white">{order.game_server_id}</span>
                    </div>
                  )}
                  <div className="pt-2 flex justify-between items-center">
                    <span className="font-mono text-xs text-muted-foreground uppercase">Total Energi</span>
                    <span className="font-mono text-lg font-bold text-primary text-glow-green">
                      {formatPrice(order.price)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-black/40 border border-primary/20 rounded-xl p-6 font-mono text-[11px] text-muted-foreground space-y-3">
                  <div className="flex items-center gap-2 text-primary font-bold">
                    <Activity className="w-4 h-4" />
                    <span>LOG DIAGNOSTIK REACTOR</span>
                  </div>
                  <p>▸ PRESSURE: 14.2 BAR [NOMINAL]</p>
                  <p>▸ TRANSAKSI BERHASIL DIDUKUNG VALVE UTAMA</p>
                  <p>▸ NOTIFIKASI DIKIRIM KE: {order.user_email}</p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-border text-white hover:bg-white/5 font-mono text-xs py-6"
                    onClick={() => navigate('/track')}
                  >
                    [ Cek Transaksi ]
                  </Button>
                  <Button
                    className="flex-1 bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest neon-green text-xs py-6"
                    onClick={() => navigate('/')}
                  >
                    Kembali ke Home
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : !paymentData ? (
          /* Payment Method Selection */
          <div>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">TRANSMISSION_INIT</span>
              </div>
              <h1 className="font-bold text-3xl text-white uppercase mb-2 tracking-wider">
                Pilih Jalur Energi
              </h1>
              <p className="text-muted-foreground font-mono text-sm">
                Total Muatan: <span className="text-primary font-bold text-glow-green">{formatPrice(order.price)}</span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 items-start">
              
              {/* Left Column: Form & Channels */}
              <div className="lg:col-span-7 space-y-6">
                {/* Order Summary */}
                <div className="bg-card/80 backdrop-blur-sm rounded-xl p-4 border border-border cyber-card relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-primary/50" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-secondary/50" />
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center neon-green">
                      <CreditCard className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-bold text-white text-sm uppercase">{order.product_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{order.denomination_name}</p>
                    </div>
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">
                    MANIFEST_NO: {order.order_number}
                  </div>
                </div>

                {/* Payment Method Tabs */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedPaymentMethod('va')}
                    className={`flex-1 py-3 px-4 rounded-lg border font-mono text-xs uppercase tracking-widest transition-all duration-200 ${
                      selectedPaymentMethod === 'va'
                        ? 'border-primary bg-primary/20 text-primary neon-green'
                        : 'border-border bg-card text-muted-foreground hover:border-primary/40'
                    }`}
                  >
                    <Building2 className="w-5 h-5 mx-auto mb-1" />
                    <span>Virtual Account</span>
                  </button>
                  <button
                    onClick={() => setSelectedPaymentMethod('qris')}
                    className={`flex-1 py-3 px-4 rounded-lg border font-mono text-xs uppercase tracking-widest transition-all duration-200 ${
                      selectedPaymentMethod === 'qris'
                        ? 'border-secondary bg-secondary/20 text-secondary neon-magenta'
                        : 'border-border bg-card text-muted-foreground hover:border-secondary/40'
                    }`}
                  >
                    <QrCode className="w-5 h-5 mx-auto mb-1" />
                    <span>QRIS</span>
                  </button>
                </div>

                {/* Bank Selection for VA */}
                {selectedPaymentMethod === 'va' && (
                  <div className="bg-card/80 backdrop-blur-sm rounded-xl p-4 border border-border cyber-card relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-primary/50" />
                    <p className="font-mono text-xs text-muted-foreground mb-3">▸ PILIH STASIUN BANK:</p>
                    <div className="grid grid-cols-3 gap-2">
                      {paymentChannels?.virtual_account?.map((bank) => (
                        <button
                          key={bank.code}
                          onClick={() => setSelectedBank(bank.code)}
                          className={`py-3 px-2 rounded-lg border text-center transition-all ${
                            selectedBank === bank.code
                              ? 'border-primary bg-primary/15'
                              : 'border-border bg-black/40 hover:border-primary/50'
                          }`}
                        >
                          <span 
                            className="inline-block px-2 py-1 rounded text-[10px] font-bold font-mono tracking-wider"
                            style={{ 
                              backgroundColor: BANK_INFO[bank.code]?.color || '#333',
                              color: BANK_INFO[bank.code]?.textColor || 'white'
                            }}
                          >
                            {BANK_INFO[bank.code]?.name || bank.code.toUpperCase()}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* QRIS Info */}
                {selectedPaymentMethod === 'qris' && (
                  <div className="bg-card/80 backdrop-blur-sm rounded-xl p-4 border border-border cyber-card relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-secondary/50" />
                    <div className="flex items-center gap-3">
                      <QrCode className="w-10 h-10 text-secondary animate-pulse" />
                      <div>
                        <p className="font-bold text-white text-sm font-mono">[ AETHER_QRIS ]</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          Scan dengan e-wallet & mobile banking
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Telemetry & Actions */}
              <div className="lg:col-span-5 space-y-6">
                <div className="p-4 bg-black/40 border border-primary/20 rounded-xl font-mono text-[11px] text-muted-foreground space-y-2">
                  <div className="flex items-center gap-2 text-primary font-bold">
                    <Cpu className="w-3.5 h-3.5" />
                    <span>KONEKTIVITAS PIPA</span>
                  </div>
                  <p>▸ Saluran pembayaran terhubung ke pipa generator AetherCore.</p>
                  <p>▸ Transmisi energi dilakukan instan setelah transfer tervalidasi.</p>
                </div>

                <Button
                  className="w-full bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 neon-green text-sm"
                  onClick={createPayment}
                  disabled={creatingPayment}
                  data-testid="create-payment-btn"
                >
                  {creatingPayment ? (
                    <span className="flex items-center gap-2 font-mono">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      DECRYPTING_GATEWAY...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 font-mono">
                      [ MULAI TRANSMISI ]
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </div>

            </div>
          </div>
        ) : (
          /* Payment Details State */
          <div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-4 neon-magenta animate-pulse">
                <Clock className="w-8 h-8 text-secondary" />
              </div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-secondary/30 bg-secondary/5">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                <span className="font-mono text-[10px] text-secondary tracking-widest uppercase">TRANSMISSION_PENDING</span>
              </div>
              <h1 className="font-bold text-3xl text-white uppercase mb-2 tracking-wider">
                Menunggu Pembayaran
              </h1>
              <p className="text-muted-foreground text-sm font-mono">
                Selesaikan transfer daya sebelum pipa boiler kedaluwarsa (24 jam)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 items-start">
              
              {/* Left Column: QR / VA Panel */}
              <div className="lg:col-span-7 space-y-6">
                {paymentData.payment_method === 'va' || paymentData.payment_method === 'virtual_account' ? (
                  <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
                    <div className="flex items-center gap-3 mb-4">
                      <span 
                        className="px-3 py-1.5 rounded font-bold text-xs font-mono"
                        style={{ 
                          backgroundColor: BANK_INFO[paymentData.channel]?.color || '#333',
                          color: BANK_INFO[paymentData.channel]?.textColor || 'white'
                        }}
                      >
                        {BANK_INFO[paymentData.channel]?.name || paymentData.channel?.toUpperCase()}
                      </span>
                      <span className="text-white font-mono text-xs uppercase tracking-widest">Virtual Account Node</span>
                    </div>

                    <div className="bg-black/40 rounded-lg p-4 mb-4 border border-border/50">
                      <p className="text-[10px] font-mono text-muted-foreground mb-2">REK_NODE_VIRTUAL</p>
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-lg text-white font-bold tracking-wider">
                          {paymentData.va_number}
                        </span>
                        <button
                          onClick={() => copyToClipboard(paymentData.va_number)}
                          className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2 text-xs font-mono">
                      <p className="text-muted-foreground">Instruksi Dekripsi:</p>
                      <ol className="list-decimal list-inside space-y-1 text-gray-300">
                        <li>Gunakan m-Banking atau ATM stasiun {BANK_INFO[paymentData.channel]?.name}</li>
                        <li>Arahkan ke Transfer ke Virtual Account</li>
                        <li>Input nomor VA di atas</li>
                        <li>Selesaikan verifikasi transaksi</li>
                      </ol>
                    </div>

                    {paymentData.is_mock && (
                      <p className="text-[10px] text-center text-yellow-500 mt-4 font-mono animate-pulse">
                        * PROTOKOL SIMULASI - Angka VA disimulasikan
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-secondary/50" />
                    <div className="flex items-center gap-3 mb-4">
                      <QrCode className="w-6 h-6 text-secondary animate-pulse" />
                      <span className="font-bold text-white text-xs font-mono">AETHER CORE QRIS</span>
                    </div>

                    <div className="bg-white rounded-xl p-6 mb-4 flex justify-center border border-border/50">
                      {paymentData.qr_content ? (
                        <QRCodeCanvas
                          value={paymentData.qr_content}
                          size={220}
                          level="M"
                          marginSize={2}
                        />
                      ) : (
                        <div className="w-[220px] h-[220px] flex items-center justify-center">
                          <QrCode className="w-32 h-32 text-gray-300" />
                        </div>
                      )}
                    </div>

                    <p className="text-center text-xs text-muted-foreground font-mono">
                      Scan QR code di atas menggunakan e-wallet atau aplikasi mobile banking Anda
                    </p>
                  </div>
                )}
              </div>

              {/* Right Column: Manifest Summary & Confirmation */}
              <div className="lg:col-span-5 space-y-6">
                {/* Order Summary */}
                <div className="bg-card/80 backdrop-blur-sm rounded-xl p-6 border border-border cyber-card relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary/50" />
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-secondary/50" />
                  <div className="flex items-center justify-between mb-4 border-b border-border/40 pb-3">
                    <span className="text-muted-foreground text-xs font-mono">MANIFEST ID</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-white text-xs font-bold">{order.order_number}</span>
                      <button
                        onClick={() => copyToClipboard(order.order_number)}
                        className="text-muted-foreground hover:text-primary"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3 pt-1">
                    <div className="flex justify-between text-xs font-mono">
                      <span className="text-muted-foreground">PRODUK</span>
                      <span className="text-white">{order.product_name}</span>
                    </div>
                    <div className="flex justify-between text-xs font-mono">
                      <span className="text-muted-foreground">DENOMINASI</span>
                      <span className="text-white">{order.denomination_name}</span>
                    </div>
                    <div className="flex justify-between text-xs font-mono">
                      <span className="text-muted-foreground">ID_TARGET</span>
                      <span className="text-white">{order.game_user_id}</span>
                    </div>
                  </div>

                  <div className="border-t border-border mt-4 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-white text-xs font-mono">TOTAL TRANSMISI</span>
                      <span className="font-mono text-xl font-bold text-primary text-glow-green">
                        {formatPrice(order.price)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <Button
                    className="w-full bg-primary hover:bg-primary/80 text-black font-bold uppercase tracking-widest py-6 neon-green text-sm"
                    onClick={handlePayment}
                    disabled={processing}
                    data-testid="simulate-payment-btn"
                  >
                    {processing ? (
                      <span className="flex items-center gap-2 font-mono">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        CONFIRMING_TRANSMISSION...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 font-mono">
                        Saya Sudah Membayar
                        <CheckCircle2 className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full border-border text-muted-foreground hover:text-white font-mono text-xs uppercase"
                    onClick={() => setPaymentData(null)}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Ubah Jalur Pembayaran
                  </Button>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}
